import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Divider,
  Popover,
  TextField,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UserCalenderClassesCardView from "../components/UserCalenderClassesCardView";
import themestyle from "../theme";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  GET_CLASS_CANCELLATION_REQUESTS,
  GET_CLASS_LIST,
  GET_INSTRUCTOR_BY_EMAIL,
  ME,
} from "../graphql/query";
import {
  APPROVE_CLASS_CANCELLATION_REQUEST,
  REJECT_CLASS_CANCELLATION_REQUEST,
} from "../graphql/mutation";
import { CLASS_TYPE_UI, ATTENDANCE_PAGE_TEXT } from "../utils/const";
import { GetClassCancellationRequestsResponse } from "../interfaces/attendance.interface";
import CustomSnackbar from "../components/Snackbar";
import AppDatePicker from "../components/AppDatePicker";
import { formatTo12Hour, formatTimeAgo } from "../utils/time";

type ViewMode = "day" | "week" | "month" | "year";
type AttendanceCalendarEvent = {
  id: string;
  classId: string;
  title: string;
  instructor: string;
  style: string;
  attendance: string;
  start: Date;
  end: Date;
  isCancelled: boolean;
};

const VIEW_LABELS: Record<ViewMode, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
  year: "Year",
};

const FILTER_LABELS: Record<ViewMode, string> = {
  day: "Day of",
  week: "Week of",
  month: "Month of",
  year: "Year of",
};

const toInputDate = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const toInputMonth = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const parseInputDate = (value: string, endOfDay = false) => {
  if (!value) return null;
  const parsed = new Date(
    `${value}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`,
  );
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseInputMonth = (value: string) => {
  if (!value) return null;
  const parsed = new Date(`${value}-01T00:00:00.000`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeDatePart = (value: string) => {
  if (!value) return null;
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return {
      year: Number(match[1]),
      monthIndex: Number(match[2]) - 1,
      day: Number(match[3]),
    };
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    year: parsed.getFullYear(),
    monthIndex: parsed.getMonth(),
    day: parsed.getDate(),
  };
};

const normalizeTimePart = (value: string) => {
  if (!value) return null;
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] ?? "0");
  const meridiem = match[4]?.toUpperCase();

  if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) {
    return null;
  }

  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    if (meridiem === "PM" && hours !== 12) hours += 12;
    if (meridiem === "AM" && hours === 12) hours = 0;
  }

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
    return null;
  }

  return { hours, minutes, seconds };
};

const WEEKDAY_INDEX: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const isIsoDateLike = (value: string) => /^\d{4}-\d{2}-\d{2}/.test(value.trim());

const getDatesForWeekdayInRange = (
  weekdayLabel: string,
  rangeStart: Date,
  rangeEnd: Date,
) => {
  const weekday = WEEKDAY_INDEX[weekdayLabel.trim().toLowerCase()];
  if (weekday === undefined) return [];

  const start = new Date(rangeStart);
  start.setHours(0, 0, 0, 0);
  const end = new Date(rangeEnd);
  end.setHours(0, 0, 0, 0);
  if (start > end) return [];

  const first = new Date(start);
  const dayOffset = (weekday - first.getDay() + 7) % 7;
  first.setDate(first.getDate() + dayOffset);

  const dates: string[] = [];
  const cursor = new Date(first);
  while (cursor <= end) {
    dates.push(toInputDate(cursor));
    cursor.setDate(cursor.getDate() + 7);
  }
  return dates;
};

const startOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfWeek = (date: Date) => {
  const start = startOfWeek(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

const AttendancePage: React.FC = () => {
  useEffect(() => {
    document.title = ATTENDANCE_PAGE_TEXT.PAGE_TITLE;
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<"calendar" | "cancellations">(
    "calendar",
  );
  const [view, setView] = useState<ViewMode>("month");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dayFilterDate, setDayFilterDate] = useState(toInputDate(new Date()));
  const [weekFilterDate, setWeekFilterDate] = useState(toInputDate(new Date()));
  const [monthFilterValue, setMonthFilterValue] = useState(
    toInputMonth(new Date()),
  );
  const [yearFilterValue, setYearFilterValue] = useState(
    String(new Date().getFullYear()),
  );
  const [eventAnchor, setEventAnchor] = useState<HTMLElement | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedDayForFilter, setSelectedDayForFilter] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<
    "success" | "error" | "info"
  >("success");
  const { data: meData } = useQuery(ME, { fetchPolicy: "cache-first" });
  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();
  const userRole =
    meData?.me?.role?.toLowerCase() || storedUser?.role?.toLowerCase() || "";
  const userEmail = meData?.me?.email || storedUser?.email || "";

  useEffect(() => {
    if (userRole === "user") {
      setView("week");
      setSelectedDayForFilter((prev) => prev || toInputDate(new Date()));
    }
  }, [userRole]);

  useEffect(() => {
    const selectedDate = (location.state as any)?.selectedDate;
    if (!selectedDate) return;

    const parsedDate = parseInputDate(selectedDate);
    if (!parsedDate) return;

    setCurrentDate(parsedDate);
    setWeekFilterDate(selectedDate);
    setDayFilterDate(selectedDate);

    if (userRole === "user") {
      setView("week");
      setSelectedDayForFilter(selectedDate);
    }
  }, [location.state, userRole]);

  useEffect(() => {
    const requestedTab = (location.state as any)?.activeTab;
    if (requestedTab === "calendar" || requestedTab === "cancellations") {
      setActiveTab(requestedTab);
    }
  }, [location.state]);

  const isInstructor = userRole === "instructor";
  const { data: instructorData } = useQuery(GET_INSTRUCTOR_BY_EMAIL, {
    variables: { search: userEmail },
    skip: !isInstructor || !userEmail,
    fetchPolicy: "network-only",
  });
  const loggedInInstructorId = instructorData?.getInstructorList?.data?.[0]?.id;
  const classListInstructorId = isInstructor
    ? loggedInInstructorId || "__NO_MATCH__"
    : undefined;
  const { data: classData } = useQuery(GET_CLASS_LIST, {
    variables: {
      page: 1,
      perPage: 1000,
      search: "",
      includeCancelled: true,
      instructorId: classListInstructorId,
    },
    fetchPolicy: "network-only",
  });
  const canReviewCancellation =
    userRole === "admin" || userRole === "super_admin";

  const {
    data: cancellationData,
    loading: cancellationLoading,
    refetch: refetchCancellations,
  } = useQuery<GetClassCancellationRequestsResponse>(
    GET_CLASS_CANCELLATION_REQUESTS,
    {
      skip: !canReviewCancellation,
      fetchPolicy: "network-only",
    },
  );

  const [approveCancellationRequest, { loading: approvingCancellation }] =
    useMutation(APPROVE_CLASS_CANCELLATION_REQUEST);
  const [rejectCancellationRequest, { loading: rejectingCancellation }] =
    useMutation(REJECT_CLASS_CANCELLATION_REQUEST);

  const cancellationRequests =
    cancellationData?.getClassCancellationRequests?.data || [];
  const reviewingCancellation =
    approvingCancellation || rejectingCancellation;

  const handlePrev = () => {
    const next = new Date(currentDate);
    if (view === "month") next.setMonth(next.getMonth() - 1);
    if (view === "week") next.setDate(next.getDate() - 7);
    if (view === "day") next.setDate(next.getDate() - 1);
    if (view === "year") next.setFullYear(next.getFullYear() - 1);
    setCurrentDate(next);
    if (userRole === "user" && selectedDayForFilter && view === "week") {
      const parsedDay = parseInputDate(selectedDayForFilter);
      if (parsedDay) {
        parsedDay.setDate(parsedDay.getDate() - 7);
        setSelectedDayForFilter(toInputDate(parsedDay));
      }
    } else {
      setSelectedDayForFilter(null);
    }
    if (view === "day") {
      setDayFilterDate(toInputDate(next));
    }
    if (view === "week") {
      setWeekFilterDate(toInputDate(next));
    }
    if (view === "month") {
      setMonthFilterValue(toInputMonth(next));
    }
    if (view === "year") {
      setYearFilterValue(String(next.getFullYear()));
    }
  };

  const handleNext = () => {
    const next = new Date(currentDate);
    if (view === "month") next.setMonth(next.getMonth() + 1);
    if (view === "week") next.setDate(next.getDate() + 7);
    if (view === "day") next.setDate(next.getDate() + 1);
    if (view === "year") next.setFullYear(next.getFullYear() + 1);
    setCurrentDate(next);
    if (userRole === "user" && selectedDayForFilter && view === "week") {
      const parsedDay = parseInputDate(selectedDayForFilter);
      if (parsedDay) {
        parsedDay.setDate(parsedDay.getDate() + 7);
        setSelectedDayForFilter(toInputDate(parsedDay));
      }
    } else {
      setSelectedDayForFilter(null);
    }
    if (view === "day") {
      setDayFilterDate(toInputDate(next));
    }
    if (view === "week") {
      setWeekFilterDate(toInputDate(next));
    }
    if (view === "month") {
      setMonthFilterValue(toInputMonth(next));
    }
    if (view === "year") {
      setYearFilterValue(String(next.getFullYear()));
    }
  };

  const sampleEvents = useMemo<AttendanceCalendarEvent[]>(() => {
    const classes = classData?.getClassList?.data || [];
    const parseDateTime = (day: string, time: string) => {
      if (!day || !time) return null;
      const normalizedDate = normalizeDatePart(day);
      const normalizedTime = normalizeTimePart(time);
      if (!normalizedDate || !normalizedTime) return null;
      const date = new Date(
        normalizedDate.year,
        normalizedDate.monthIndex,
        normalizedDate.day,
        normalizedTime.hours,
        normalizedTime.minutes,
        normalizedTime.seconds,
      );
      return Number.isNaN(date.getTime()) ? null : date;
    };
    const getActiveRange = () => {
      if (view === "day") {
        const selected = parseInputDate(dayFilterDate) || currentDate;
        const start = new Date(selected);
        start.setHours(0, 0, 0, 0);
        const end = new Date(selected);
        end.setHours(23, 59, 59, 999);
        return { start, end };
      }
      if (view === "week") {
        const selected = parseInputDate(weekFilterDate) || currentDate;
        return { start: startOfWeek(selected), end: endOfWeek(selected) };
      }
      if (view === "month") {
        const selected = parseInputMonth(monthFilterValue) || currentDate;
        const start = new Date(selected.getFullYear(), selected.getMonth(), 1, 0, 0, 0, 0);
        const end = new Date(selected.getFullYear(), selected.getMonth() + 1, 0, 23, 59, 59, 999);
        return { start, end };
      }
      const parsedYear = Number(yearFilterValue);
      const targetYear = Number.isNaN(parsedYear)
        ? currentDate.getFullYear()
        : parsedYear;
      return {
        start: new Date(targetYear, 0, 1, 0, 0, 0, 0),
        end: new Date(targetYear, 11, 31, 23, 59, 59, 999),
      };
    };
    const { start: activeStart, end: activeEnd } = getActiveRange();

    return classes.flatMap((cls: any) => {
      // Use scheduledDates array if available; otherwise resolve legacy `day`.
      let dates: string[] =
        Array.isArray(cls.scheduledDates) && cls.scheduledDates.length > 0
          ? cls.scheduledDates
          : cls.day
            ? [cls.day]
            : [];

      // Legacy records can store weekday names (e.g. "Thursday") instead of YYYY-MM-DD.
      if (
        dates.length === 1 &&
        cls.day &&
        typeof cls.day === "string" &&
        !isIsoDateLike(cls.day)
      ) {
        dates = getDatesForWeekdayInRange(cls.day, activeStart, activeEnd);
      }

      const enrolledCount = Array.isArray(cls.enrolledStudents)
        ? cls.enrolledStudents.length
        : 0;

      return dates
        .map((dateStr: string) => {
          const start = parseDateTime(dateStr, cls.startTime);
          const end = parseDateTime(dateStr, cls.endTime);
          if (!start || !end) return null;

          return {
            id: `${cls.id}-${dateStr}-${cls.startTime}`,
            classId: cls.id,
            title: cls.className,
            instructor: cls.instructorName,
            style: CLASS_TYPE_UI[cls.classType]?.label || cls.classType || "",
            attendance: `${enrolledCount}/30`,
            start,
            end,
            isCancelled: Boolean(cls.isCancelled) || (Array.isArray(cls.cancelledDates) && cls.cancelledDates.includes(dateStr)),
          } as AttendanceCalendarEvent;
        })
        .filter(
          (
            event: AttendanceCalendarEvent | null,
          ): event is AttendanceCalendarEvent => Boolean(event),
        );
    });
  }, [
    classData,
    view,
    currentDate,
    dayFilterDate,
    weekFilterDate,
    monthFilterValue,
    yearFilterValue,
  ]);

  const monthGridDates = useMemo(() => {
    const first = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1,
    );
    const start = startOfWeek(first);
    const last = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0,
    );
    const end = endOfWeek(last);
    const days: Date[] = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [currentDate]);

  const weekDates = useMemo(() => {
    const start = startOfWeek(currentDate);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [currentDate]);

  const formatMonthYear = (date: Date) =>
    date.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const formatWeekRange = (date: Date) => {
    const start = startOfWeek(date);
    const end = endOfWeek(date);
    const startStr = start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const endStr = end.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${startStr} - ${endStr}`;
  };

  const filteredEvents = useMemo(() => {
    if (view === "day") {
      const selected = parseInputDate(dayFilterDate);
      if (!selected) return sampleEvents;
      return sampleEvents.filter(
        (event) =>
          event.start.getFullYear() === selected.getFullYear() &&
          event.start.getMonth() === selected.getMonth() &&
          event.start.getDate() === selected.getDate(),
      );
    }
    if (view === "week") {
      const selected = parseInputDate(weekFilterDate) || currentDate;
      const weekStart = startOfWeek(selected);
      const weekEnd = endOfWeek(selected);
      return sampleEvents.filter(
        (event) => event.start >= weekStart && event.start <= weekEnd,
      );
    }
    if (view === "month") {
      const selected = parseInputMonth(monthFilterValue) || currentDate;
      return sampleEvents.filter(
        (event) =>
          event.start.getFullYear() === selected.getFullYear() &&
          event.start.getMonth() === selected.getMonth(),
      );
    }
    if (view === "year") {
      const parsedYear = Number(yearFilterValue);
      const targetYear = Number.isNaN(parsedYear)
        ? currentDate.getFullYear()
        : parsedYear;
      return sampleEvents.filter(
        (event) => event.start.getFullYear() === targetYear,
      );
    }

    const start = parseInputDate(fromDate);
    const end = parseInputDate(toDate, true);

    if (!start && !end) return sampleEvents;

    return sampleEvents.filter((event) => {
      if (start && event.start < start) return false;
      if (end && event.start > end) return false;
      return true;
    });
  }, [
    sampleEvents,
    view,
    dayFilterDate,
    weekFilterDate,
    monthFilterValue,
    yearFilterValue,
    currentDate,
    fromDate,
    toDate,
  ]);

  const eventsForDay = (date: Date) =>
    filteredEvents.filter(
      (e) =>
        e.start.getFullYear() === date.getFullYear() &&
        e.start.getMonth() === date.getMonth() &&
        e.start.getDate() === date.getDate(),
    );

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const selectedEvent = selectedEventId
    ? filteredEvents.find((event) => event.id === selectedEventId) || null
    : null;

  const handleReviewCancellation = async (
    requestId: string,
    decision: "approve" | "reject",
  ) => {
    try {
      const { data } = await (decision === "approve"
        ? approveCancellationRequest({
            variables: { id: requestId },
          })
        : rejectCancellationRequest({
            variables: { id: requestId },
          }));

      const result =
        decision === "approve"
          ? data?.approveClassCancellationRequest
          : data?.rejectClassCancellationRequest;
      const success = result?.success;
      const successMessage =
        decision === "approve"
          ? "Cancellation approved"
          : "Cancellation rejected";
      const errorMessage =
        decision === "approve" ? "Failed to approve" : "Failed to reject";

      setSnackbarMessage(result?.message || (success ? successMessage : errorMessage));
      setSnackbarType(success ? "success" : "error");
      setSnackbarOpen(true);
      if (success) {
        refetchCancellations();
      }
    } catch (error: any) {
      setSnackbarMessage(
        error?.message ||
          (decision === "approve" ? "Failed to approve" : "Failed to reject"),
      );
      setSnackbarType("error");
      setSnackbarOpen(true);
    }
  };

  const formatReviewDetails = (request: {
    reviewedByName?: string | null;
    reviewedByRole?: string | null;
    reviewedAt?: string | null;
  }) => {
    if (!request.reviewedByName) return null;

    const reviewedAtText = request.reviewedAt
      ? new Date(request.reviewedAt).toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : null;
    return `Reviewed by ${request.reviewedByName}${
      request.reviewedByRole ? ` (${request.reviewedByRole.replace("_", " ")})` : ""
    }${reviewedAtText ? ` on ${reviewedAtText}` : ""}`;
  };

  return (
    <Box sx={{ mt: { xs: 1, sm: 3, md: 3 }, px: { xs: 1, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 0 },
        }}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{ 
            fontSize: "24px",
            alignSelf: { xs: "flex-start", md: "auto" },
            width: { xs: "100%", md: "auto" }
          }}
          fontFamily={themestyle.fontFamily.Roboto}
        >
          Calendar
        </Typography>

        {canReviewCancellation && (
          <Box
            sx={{
              display: "flex",
              backgroundColor: themestyle.colors.white,
              borderRadius: "10px",
              padding: "6px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.05)",
              width: { xs: "100%", md: "fit-content" },
              justifyContent: { xs: "space-between", md: "flex-start" },
            }}
          >
            <Box
              onClick={() => setActiveTab("calendar")}
              sx={{
                cursor: "pointer",
                flex: 1,
                textAlign: "center",
                px: { xs: 2, sm: 4 },
                py: { xs: 1.2, sm: 1.2 },
                fontSize: { xs: "14px", sm: "16px" },
                borderRadius: "8px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: activeTab === "calendar" ? "#4CAF50" : "#6B7280",
                backgroundColor:
                  activeTab === "calendar" ? "#EEF7E9" : "transparent",
                transition: "all 0.3s ease",
              }}
            >
              Calendar
            </Box>
            {canReviewCancellation && (
              <Box
                onClick={() => setActiveTab("cancellations")}
                sx={{
                  cursor: "pointer",
                  flex: 1,
                  textAlign: "center",
                  px: { xs: 2, sm: 4 },
                  py: { xs: 1.2, sm: 1.2 },
                  fontSize: { xs: "14px", sm: "16px" },
                  borderRadius: "8px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  color: activeTab === "cancellations" ? "#4CAF50" : "#6B7280",
                  backgroundColor:
                    activeTab === "cancellations" ? "#EEF7E9" : "transparent",
                  transition: "all 0.3s ease",
                }}
              >
                Cancellation Requests
              </Box>
            )}
          </Box>
        )}
      </Box>

      {activeTab === "cancellations" && canReviewCancellation ? (
        <Box
          sx={{
            bgcolor: themestyle.colors.white,
            borderRadius: "12px",
            p: { xs: 3, md: 4 },
            boxShadow: "0px 6px 16px rgba(0,0,0,0.06)",
          }}
        >
          <Typography fontWeight={700} sx={{ mb: 2, fontSize: "18px" }}>
            Cancellation Requests
          </Typography>
          {cancellationLoading ? (
            <Typography sx={{ color: "#6B7280" }}>
              Loading requests...
            </Typography>
          ) : cancellationRequests.length === 0 ? (
            <Typography sx={{ color: "#6B7280" }}>
              No cancellation requests yet.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {cancellationRequests.map((request) => {
                const isPending = request.status === "PENDING";
                const reviewDetails = formatReviewDetails(request);
                return (
                  <Box
                    key={request.id}
                    sx={{
                      border: "1px solid #E5E7EB",
                      borderRadius: "10px",
                      p: 2,
                      backgroundColor: "#fff",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {request.className}
                      </Typography>
                      {request.created_at && (
                        <Typography sx={{ color: "#9CA3AF", fontSize: "11px", fontWeight: 500, fontStyle: "italic", textAlign: "right" }}>
                          Requested {formatTimeAgo(request.created_at)}
                          <br />
                          ({new Date(request.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} | {new Date(request.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })})
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 3, mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <PersonOutlineIcon sx={{ fontSize: "16px", color: "#6B7280" }} />
                          <Typography sx={{ color: "#4B5563", fontSize: "13px", fontWeight: 600 }}>
                            Instructor: {request.instructorName}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <MailOutlineIcon sx={{ fontSize: "16px", color: "#6B7280" }} />
                          <Typography sx={{ color: "#6B7280", fontSize: "13px" }}>
                            Requested by: {request.requestedByName} ({request.requestedByEmail})
                          </Typography>
                        </Box>
                      </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <CalendarTodayIcon sx={{ fontSize: "16px", color: "#6B7280" }} />
                            <Typography sx={{ color: "#4B5563", fontSize: "13px", fontWeight: 700 }}>
                              Date: {request.sessionDate}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: "16px", color: "#6B7280" }} />
                            <Typography sx={{ color: "#4B5563", fontSize: "13px", fontWeight: 700 }}>
                              Time: {formatTo12Hour(request.startTime)} - {formatTo12Hour(request.endTime)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2, bgcolor: "#F9FAFB", p: 1.5, borderRadius: "8px" }}>
                        <InfoOutlinedIcon sx={{ fontSize: "16px", color: "#6B7280", mt: 0.2 }} />
                        <Typography sx={{ color: "#374151", fontSize: "14px", fontStyle: "italic" }}>
                          Reason: {request.reason}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", sm: "center" },
                          pt: 1,
                          borderTop: "1px solid #F3F4F6",
                          gap: 1.5,
                          flexDirection: { xs: "column", sm: "row" },
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor:
                                  request.status === "APPROVED"
                                    ? "#15803D"
                                    : request.status === "REJECTED"
                                      ? "#B91C1C"
                                      : "#F59E0B",
                              }}
                            />
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: "12px",
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                                color:
                                  request.status === "APPROVED"
                                    ? "#15803D"
                                    : request.status === "REJECTED"
                                      ? "#B91C1C"
                                      : "#D97706",
                              }}
                            >
                              {request.status}
                            </Typography>
                          </Box>
                          {reviewDetails ? (
                            <Typography sx={{ color: "#6B7280", fontSize: "12px" }}>
                              {reviewDetails}
                            </Typography>
                          ) : null}
                        </Box>
                        {isPending && (
                          <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}>
                            <Button
                              variant="outlined"
                              onClick={() => handleReviewCancellation(request.id, "reject")}
                              disabled={reviewingCancellation}
                              sx={{
                                textTransform: "none",
                                color: "#B91C1C",
                                borderColor: "#FCA5A5",
                                borderRadius: "8px",
                                px: 2.5,
                                fontWeight: 700,
                                width: { xs: "50%", sm: "auto" },
                                "&:hover": {
                                  borderColor: "#EF4444",
                                  backgroundColor: "#FEF2F2",
                                },
                              }}
                            >
                              Reject
                            </Button>
                            <Button
                              variant="contained"
                              onClick={() => handleReviewCancellation(request.id, "approve")}
                              disabled={reviewingCancellation}
                              sx={{
                                textTransform: "none",
                                backgroundColor: themestyle.colors.activeGreen,
                                borderRadius: "8px",
                                px: 3,
                                fontWeight: 700,
                                width: { xs: "50%", sm: "auto" },
                                boxShadow: "0px 4px 12px rgba(90, 168, 57, 0.2)",
                                "&:hover": {
                                  backgroundColor: themestyle.colors.activeGreen,
                                  boxShadow: "0px 6px 16px rgba(90, 168, 57, 0.3)",
                                },
                              }}
                            >
                              Approve Cancellation
                            </Button>
                          </Box>
                        )}
                      </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: themestyle.colors.white,
            borderRadius: "12px",
            boxShadow: "0px 8px 20px rgba(15, 23, 42, 0.06)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography fontWeight={600} sx={{ fontSize: "18px" }}>
                {view === "month"
                  ? formatMonthYear(currentDate)
                  : view === "week"
                    ? formatWeekRange(currentDate)
                    : view === "day"
                      ? currentDate.toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })
                      : currentDate.getFullYear()}
              </Typography>
              <IconButton onClick={handlePrev} sx={{ color: "#6B7280" }}>
                <ChevronLeftIcon />
              </IconButton>
              <IconButton onClick={handleNext} sx={{ color: "#6B7280" }}>
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {userRole !== "user" && (
              <Box>
                <Button
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  variant="outlined"
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    borderRadius: "999px",
                    textTransform: "none",
                    color: "#1F2937",
                    borderColor: "#D1D5DB",
                    fontWeight: 600,
                    px: 2.5,
                    bgcolor: "#fff",
                    "&:hover": {
                      bgcolor: "#F9FAFB",
                      borderColor: "#CBD5E1",
                    },
                  }}
                >
                  {VIEW_LABELS[view]}
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                >
                  {(["day", "week", "month", "year"] as ViewMode[]).map((v) => (
                    <MenuItem
                      key={v}
                      onClick={() => {
                        setView(v);
                        if (v === "day") {
                          setDayFilterDate(toInputDate(currentDate));
                        }
                        if (v === "week") {
                          setWeekFilterDate(toInputDate(currentDate));
                        }
                        if (v === "month") {
                          setMonthFilterValue(toInputMonth(currentDate));
                        }
                        if (v === "year") {
                          setYearFilterValue(String(currentDate.getFullYear()));
                        }
                        setAnchorEl(null);
                      }}
                    >
                      {VIEW_LABELS[v]}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}

            {view === "day" ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {userRole !== "user" && (
                  <Typography
                    sx={{ fontSize: "13px", color: "#64748B", fontWeight: 700 }}
                  >
                    {FILTER_LABELS.day}
                  </Typography>
                )}
                <AppDatePicker
                  value={dayFilterDate}
                  onChange={(v) => {
                    setDayFilterDate(v);
                    const parsed = parseInputDate(v);
                    if (parsed) {
                      setCurrentDate(parsed);
                    }
                  }}
                />
              </Box>
            ) : view === "week" ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {userRole !== "user" && (
                  <Typography
                    sx={{ fontSize: "13px", color: "#64748B", fontWeight: 700 }}
                  >
                    {FILTER_LABELS.week}
                  </Typography>
                )}
                <AppDatePicker
                  value={weekFilterDate}
                  onChange={(v) => {
                    setWeekFilterDate(v);
                    setSelectedDayForFilter(v);
                    const parsed = parseInputDate(v);
                    if (parsed) {
                      setCurrentDate(parsed);
                    }
                  }}
                />
              </Box>
            ) : view === "month" ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {userRole !== "user" && (
                  <Typography
                    sx={{ fontSize: "13px", color: "#64748B", fontWeight: 700 }}
                  >
                    {FILTER_LABELS.month}
                  </Typography>
                )}
                <TextField
                  type="month"
                  size="small"
                  value={monthFilterValue}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setMonthFilterValue(nextValue);
                    const parsed = parseInputMonth(nextValue);
                    if (parsed) {
                      setCurrentDate(parsed);
                    }
                  }}
                  sx={{
                    minWidth: 190,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "#fff",
                      boxShadow: "0 6px 16px rgba(15, 23, 42, 0.10)",
                    },
                  }}
                />
              </Box>
            ) : view === "year" ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {userRole !== "user" && (
                  <Typography
                    sx={{ fontSize: "13px", color: "#64748B", fontWeight: 700 }}
                  >
                    {FILTER_LABELS.year}
                  </Typography>
                )}
                <TextField
                  type="number"
                  size="small"
                  value={yearFilterValue}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setYearFilterValue(nextValue);
                    const parsedYear = Number(nextValue);
                    if (
                      !Number.isNaN(parsedYear) &&
                      parsedYear >= 1900 &&
                      parsedYear <= 2100
                    ) {
                      setCurrentDate(
                        new Date(parsedYear, currentDate.getMonth(), 1),
                      );
                    }
                  }}
                  inputProps={{ min: 1900, max: 2100, step: 1 }}
                  sx={{
                    width: 150,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "#fff",
                      boxShadow: "0 6px 16px rgba(15, 23, 42, 0.10)",
                    },
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AppDatePicker
                  value={fromDate}
                  onChange={(v) => {
                    setFromDate(v);
                    if (toDate && v && v > toDate) {
                      setToDate(v);
                    }
                  }}
                />
                <AppDatePicker
                  value={toDate}
                  minDate={fromDate || undefined}
                  onChange={(v) => {
                    setToDate(v);
                    if (fromDate && v && v < fromDate) {
                      setFromDate(v);
                    }
                  }}
                />
              </Box>
            )}
          </Box>

          <Divider />

          {view === "month" && (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: "1px",
                  backgroundColor: "#E5E7EB",
                }}
              >
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <Box
                    key={d}
                    sx={{
                      bgcolor: "#F8FAFC",
                      p: 1,
                      fontWeight: 600,
                      color: "#6B7280",
                      fontSize: "12px",
                      textAlign: "left",
                      borderBottom: "1px solid #E5E7EB",
                      minWidth: 0,
                    }}
                  >
                    {d}
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: "1px",
                  backgroundColor: "#E5E7EB",
                }}
              >
                {monthGridDates.map((date) => {
                  const inMonth = date.getMonth() === currentDate.getMonth();
                  const dayEvents = eventsForDay(date);
                  const isToday = isSameDay(date, new Date());
                  return (
                    <Box
                      key={date.toISOString()}
                      sx={{
                        bgcolor: "#fff",
                        minHeight: 120,
                        p: 1,
                        opacity: inMonth ? 1 : 0.45,
                        border: "1px solid #F1F5F9",
                        position: "relative",
                        transition: "background 0.2s ease",
                        "&:hover": {
                          backgroundColor: "#F9FAFB",
                        },
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: isToday ? "#3B82F6" : "transparent",
                            color: isToday ? "#fff" : "#111827",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: "12px",
                          }}
                        >
                          {date.getDate()}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          mt: 0.5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        {dayEvents.map((event) => (
                          <Box
                            key={event.id}
                            onClick={(e) => {
                              setSelectedEventId(event.id);
                              setEventAnchor(e.currentTarget);
                            }}
                            sx={{
                              bgcolor: event.isCancelled
                                ? "#FEF2F2"
                                : "#E8F3FF",
                              color: event.isCancelled ? "#B91C1C" : "#1D4ED8",
                              px: 1,
                              py: "2px",
                              borderRadius: "6px",
                              fontSize: "11px",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              cursor: "pointer",
                              border: event.isCancelled
                                ? "1px solid #FECACA"
                                : "1px solid #CFE3FF",
                              "&:hover": {
                                bgcolor: event.isCancelled
                                  ? "#FEE2E2"
                                  : "#DCEBFF",
                              },
                            }}
                          >
                            {event.isCancelled
                              ? `[CANCELLED] ${event.title}`
                              : event.title}{" "}
                            <span
                              style={{
                                fontWeight: 500,
                                color: event.isCancelled
                                  ? "#991B1B"
                                  : "#2563EB",
                              }}
                            >
                              {event.start.toLocaleTimeString("en-US", {
                                hour: "numeric",
                                minute: "2-digit",
                              })}
                            </span>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {view === "week" && (
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  overflowX: "auto",
                  scrollBehavior: "smooth",
                  pb: 1,
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                }}
              >
                {weekDates.map((date) => {
                  const dayEvents = eventsForDay(date);
                  const isToday = isSameDay(date, new Date());
                  const dateStr = toInputDate(date);
                  const isSelectedForFilter = selectedDayForFilter === dateStr;
                  
                  return (
                    <Box
                      key={toInputDate(date)}
                      onClick={() => {
                        if (userRole === "user") {
                          setSelectedDayForFilter(isSelectedForFilter ? toInputDate(new Date()) : dateStr);
                        }
                      }}
                      sx={{
                        flex: { xs: "0 0 120px", md: "1 1 0px" },
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        p: 1.5,
                        cursor: userRole === "user" ? "pointer" : "default",
                        bgcolor: isSelectedForFilter 
                          ? "#92D026" 
                          : isToday 
                            ? (userRole === "user" ? "#E8F3D1" : "#EFF6FF")
                            : "#fff",
                        color: isSelectedForFilter ? "#fff" : "inherit",
                        transition: "all 0.2s ease",
                        "&:hover": userRole === "user" ? {
                          bgcolor: isSelectedForFilter ? "#92D026" : "#f3f4f6",
                          transform: "translateY(-2px)"
                        } : {}
                      }}
                    >
                      <Typography
                        sx={{ 
                          fontWeight: 600, 
                          fontSize: "13px", 
                          mb: 0.5,
                          color: isSelectedForFilter ? "#fff" : "inherit"
                        }}
                      >
                        {date.toLocaleDateString("en-US", {
                          weekday: "short",
                          day: "numeric",
                        })}
                      </Typography>
                      {dayEvents.length === 0 ? (
                        <Typography sx={{ fontSize: "12px", color: isSelectedForFilter ? "rgba(255,255,255,0.8)" : "#9CA3AF" }}>
                          No classes
                        </Typography>
                      ) : userRole === "user" ? (
                        <Typography sx={{ fontSize: "12px", color: isSelectedForFilter ? "rgba(255,255,255,0.8)" : "#9CA3AF" }}>
                          {dayEvents.length} Class{dayEvents.length === 1 ? "" : "es"}
                        </Typography>
                      ) : (
                        dayEvents.map((event) => (
                          <Box
                            key={event.id}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent card click
                              setSelectedEventId(event.id);
                              setEventAnchor(e.currentTarget);
                            }}
                            sx={{
                              bgcolor: event.isCancelled
                                ? "#FEF2F2"
                                : "#E8F3FF",
                              color: event.isCancelled ? "#B91C1C" : "#1D4ED8",
                              px: 1,
                              py: "4px",
                              borderRadius: "8px",
                              fontSize: "11px",
                              fontWeight: 600,
                              mb: 0.5,
                              cursor: "pointer",
                              border: event.isCancelled
                                ? "1px solid #FECACA"
                                : "1px solid #CFE3FF",
                            }}
                          >
                            {event.isCancelled
                              ? `[CANCELLED] ${event.title}`
                              : event.title}{" "}
                            •{" "}
                            {event.start.toLocaleTimeString("en-US", {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </Box>
                        ))
                      )}
                    </Box>
                  );
                })}
              </Box>

              {userRole === "user" && (
                <UserCalenderClassesCardView
                  startDate={selectedDayForFilter || toInputDate(weekDates[0])}
                  endDate={selectedDayForFilter || toInputDate(weekDates[weekDates.length - 1])}
                  selectedDayForFilter={selectedDayForFilter}
                  onView={(id) => navigate(`/calendar/${id}`)}
                />
              )}
            </Box>
          )}

          {view === "day" && (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography sx={{ fontWeight: 600, mb: 2 }}>
                {currentDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </Typography>
              {eventsForDay(currentDate).length === 0 ? (
                <Typography sx={{ color: "#6B7280" }}>
                  No classes scheduled.
                </Typography>
              ) : (
                eventsForDay(currentDate).map((event) => (
                  <Box
                    key={event.id}
                    onClick={(e) => {
                      setSelectedEventId(event.id);
                      setEventAnchor(e.currentTarget);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: event.isCancelled
                        ? "1px solid #FECACA"
                        : "1px solid #E5E7EB",
                      borderRadius: "10px",
                      p: 2,
                      mb: 1.5,
                      cursor: "pointer",
                      bgcolor: event.isCancelled ? "#FEF2F2" : "#F9FAFB",
                    }}
                  >
                    <Typography fontWeight={600}>
                      {event.isCancelled
                        ? `[CANCELLED] ${event.title}`
                        : event.title}
                    </Typography>
                    <Typography sx={{ color: "#6B7280" }}>
                      {event.start.toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                      })}{" "}
                      -{" "}
                      {event.end.toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          )}

          {view === "year" && (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(1, 1fr)",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 2,
                }}
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const date = new Date(currentDate.getFullYear(), i, 1);
                  const monthEvents = filteredEvents.filter(
                    (e) =>
                      e.start.getMonth() === i &&
                      e.start.getFullYear() === currentDate.getFullYear(),
                  );
                  return (
                    <Box
                      key={i}
                      sx={{
                        border: "1px solid #E5E7EB",
                        borderRadius: "12px",
                        p: 2,
                        background: "#fff",
                      }}
                    >
                      <Typography fontWeight={700} sx={{ mb: 1 }}>
                        {date.toLocaleDateString("en-US", { month: "long" })}
                      </Typography>
                      <Typography sx={{ color: "#6B7280", fontSize: "13px" }}>
                        {monthEvents.length} class
                        {monthEvents.length === 1 ? "" : "es"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Popover
        open={Boolean(eventAnchor && selectedEvent)}
        anchorEl={eventAnchor}
        onClose={() => {
          setEventAnchor(null);
          setSelectedEventId(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 2.5,
            borderRadius: "16px",
            border: "1px solid #E5E7EB",
            boxShadow: "0px 16px 40px rgba(15, 23, 42, 0.18)",
            minWidth: 280,
            background: "#F8FAFC",
          },
        }}
      >
        {selectedEvent && (
          <Box sx={{ position: "relative", pr: 3 }}>
            <IconButton
              size="small"
              onClick={() => {
                setEventAnchor(null);
                setSelectedEventId(null);
              }}
              sx={{ position: "absolute", top: -6, right: -6 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: "#1D4ED8",
                }}
              />
              <Typography fontWeight={700}>{selectedEvent.title}</Typography>
              {selectedEvent.isCancelled && (
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#B91C1C",
                    border: "1px solid #FECACA",
                    backgroundColor: "#FEF2F2",
                    px: 1,
                    py: 0.2,
                    borderRadius: "999px",
                  }}
                >
                  CANCELLED
                </Typography>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  if (!selectedEvent) return;
                  setEventAnchor(null);
                  setSelectedEventId(null);
                  const targetId = selectedEvent.classId || selectedEvent.id;
                  navigate(`/calendar/${encodeURIComponent(targetId)}`, {
                    state: { event: selectedEvent },
                  });
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: "999px",
                  fontSize: "11px",
                  px: 1.5,
                  borderColor: "#CBD5E1",
                  color: "#1F2937",
                  bgcolor: "#fff",
                }}
              >
                View Details
              </Button>
            </Box>
            <Typography sx={{ color: "#6B7280", fontSize: "13px", mb: 0.5 }}>
              {selectedEvent.start.toLocaleDateString("en-US", {
                weekday: "long",
              })}
              ,{" "}
              {selectedEvent.start.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
              })}
              -
              {selectedEvent.end.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
              })}
            </Typography>
            <Typography sx={{ color: "#6B7280", fontSize: "13px", mb: 0.5 }}>
              Instructor: {selectedEvent.instructor}
            </Typography>
            <Typography sx={{ color: "#6B7280", fontSize: "13px", mb: 0.5 }}>
              {selectedEvent.style}
            </Typography>
            <Typography sx={{ color: "#6B7280", fontSize: "13px" }}>
              Capacity: {selectedEvent.attendance}
            </Typography>
          </Box>
        )}
      </Popover>
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default AttendancePage;
